import { TIconType } from '@apia/icons';
import {
  getVariant,
  spacing,
  Box,
  Button,
  ButtonProps,
  Spinner,
  ThemeUICSSObject,
  ThemeUIStyleObject,
} from '@apia/theme';
import * as React from 'react';

export interface ISimpleButton extends ButtonProps {
  buttonSx?: ThemeUICSSObject;
  icon?: TIconType;
  iconColor?: string;
  iconPosition?: 'left' | 'right';
  iconSize?: number;
  isLoading?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  size?: 'sm' | 'md' | 'lg';
  variant?: string;
  spinnerSize?: number;
  type?: 'button' | 'submit' | 'reset';
}

const simpleButton: ThemeUICSSObject = {
  display: 'inline-flex',
  gap: spacing(3),
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'center',

  svg: {
    flexShrink: 0,
    path: {
      fill: 'currentColor',
    },
  },

  '.toggled': {
    backgroundColor: 'primary',
  },

  '.button__content': {
    overflowWrap: 'break-word',
  },

  /* PREPEND HERE */
};

export const SimpleButton = React.forwardRef(
  (
    {
      buttonSx,
      children = null,
      className,
      disabled = false,
      icon,
      iconColor,
      iconPosition = 'left',
      iconSize,
      id,
      isLoading,
      name,
      onClick,
      size,
      spinnerSize = 16,
      sx,
      title,
      type = 'button',
      variant = 'primary',
      ...buttonProps
    }: ISimpleButton,
    ref: React.Ref<unknown>,
  ) => {
    const loadingSx = React.useMemo<ThemeUIStyleObject>(
      () => ({
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }),
      [],
    );
    const spanSx = React.useMemo(
      () => ({
        color: isLoading ? 'transparent' : undefined,
      }),
      [isLoading],
    );
    const Icon = React.useMemo(() => icon, [icon]);

    return (
      <Button
        ref={ref as React.Ref<HTMLButtonElement>}
        id={id}
        name={name}
        title={title}
        aria-label={title}
        onClick={onClick}
        type={type}
        disabled={isLoading || disabled}
        className={className}
        {...getVariant(`${variant}${size !== 'md' && size ? `-${size}` : ''}`)}
        {...buttonProps}
        sx={{ ...simpleButton, ...buttonSx, ...sx }}
      >
        {Icon && iconPosition === 'left' && (
          <Icon title="" size={iconSize} color={iconColor} />
        )}
        <Box as="span" className="button__content" sx={spanSx}>
          {children ?? title}
        </Box>
        {Icon && iconPosition === 'right' && (
          <Icon title="" size={iconSize} color={iconColor} />
        )}
        {isLoading && (
          <Box className="spinner" sx={loadingSx}>
            <Spinner size={spinnerSize} color="inherit" />
          </Box>
        )}
      </Button>
    );
  },
);

SimpleButton.displayName = 'SimpleButton';
